import { useState, useMemo } from "react";
import { Plus, Apple, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { usePantry, type PantryRow } from "@/hooks/use-pantry";
import { EmptyState, SkeletonLoader } from "@/components/system";
import { AddPantryItemDialog } from "@/components/pantry/AddPantryItemDialog";

/**
 * Pantry surface (SPEC 3.3.1 Pantry tab).
 *
 * v1 list view grouped by location, with expiry color-coding (≤3d red,
 * ≤7d amber, otherwise neutral). Header action: + Add. Inline actions
 * (Eat / Edit / Delete) deferred to BW5+.
 *
 * Lives at /pantry as its own route — Health hub + tabbed sub-areas land
 * later (SPEC 3.3.0 Health hub deferred behind the data wiring).
 */

export function Pantry() {
  const q = usePantry();
  const [addOpen, setAddOpen] = useState(false);

  const grouped = useMemo(() => {
    const m = new Map<string, PantryRow[]>();
    for (const r of q.data ?? []) {
      const list = m.get(r.location) ?? [];
      list.push(r);
      m.set(r.location, list);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [q.data]);

  return (
    <article aria-labelledby="pantry-heading">
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: "var(--space-6)",
        }}
      >
        <div>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "var(--type-micro)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--fg-3)",
              margin: 0,
            }}
          >
            Pantry
          </p>
          <h1
            id="pantry-heading"
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "var(--type-display-m)",
              margin: "var(--space-2) 0 0",
            }}
          >
            What's on hand.
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "var(--space-2)",
            height: 38,
            padding: "0 var(--space-4)",
            background: "var(--accent)",
            color: "#0a0b0d",
            fontWeight: 600,
            fontSize: "var(--type-body-s)",
            borderRadius: "var(--radius-card)",
            border: "1px solid transparent",
            cursor: "pointer",
          }}
        >
          <Plus size={16} strokeWidth={2} />
          Add
        </button>
      </header>

      {q.isLoading ? (
        <SkeletonLoader variant="card" count={2} />
      ) : q.isError ? (
        <p role="alert" style={{ color: "var(--brick)" }}>
          {q.error instanceof Error ? q.error.message : "Couldn't load pantry."}
        </p>
      ) : grouped.length === 0 ? (
        <EmptyState
          icon={<Apple size={28} strokeWidth={1.5} />}
          headline="Pantry's empty."
          sub="Tap Add to start tracking what's on hand. The first few items unlock expiry alerts and shopping suggestions."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-8)" }}>
          {grouped.map(([loc, items]) => (
            <LocationGroup key={loc} location={loc} items={items} />
          ))}
        </div>
      )}

      <AddPantryItemDialog open={addOpen} onClose={() => setAddOpen(false)} />
    </article>
  );
}

function LocationGroup({
  location,
  items,
}: {
  location: string;
  items: PantryRow[];
}) {
  return (
    <section aria-label={location}>
      <h2
        style={{
          fontFamily: "var(--font-sans)",
          fontWeight: 500,
          fontSize: "var(--type-body-l)",
          color: "var(--fg)",
          margin: 0,
          marginBottom: "var(--space-3)",
          textTransform: "capitalize",
        }}
      >
        {location.replace(/_/g, " ")}
      </h2>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {items.map((it) => (
          <PantryRowItem key={it.id} item={it} />
        ))}
      </ul>
    </section>
  );
}

function PantryRowItem({ item }: { item: PantryRow }) {
  const exp = expiryStatus(item.expires);
  return (
    <li>
      <Link
        to={`/pantry/${item.id}`}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr max-content max-content 16px",
          gap: "var(--space-4)",
          alignItems: "baseline",
          padding: "var(--space-3) 0",
          borderBottom: "1px solid var(--hair)",
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <div>
          <div style={{ color: "var(--fg)", fontSize: "var(--type-body-s)" }}>
            {item.food_name}
            {item.staple && (
              <span
                style={{
                  fontSize: "var(--type-meta)",
                  color: "var(--accent)",
                  marginLeft: "var(--space-2)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                · staple
              </span>
            )}
          </div>
          {item.food_brand && (
            <div
              style={{
                color: "var(--fg-3)",
                fontSize: "var(--type-meta)",
                marginTop: 2,
              }}
            >
              {item.food_brand}
            </div>
          )}
          {(item.lookup_source === "ai" || item.lookup_source === "photo_label") && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginTop: 3,
                padding: "1px 6px",
                background: "var(--chip)",
                borderRadius: "var(--radius-chip)",
                fontSize: "var(--type-meta)",
                color: "var(--fg-3)",
              }}
            >
              {item.lookup_source === "ai" ? "rough estimate" : "label scan"}
            </div>
          )}
        </div>
        <span
          className="mono"
          style={{ color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}
        >
          {item.qty} {item.unit}
        </span>
        <span
          className="mono"
          style={{
            color: exp.color,
            fontSize: "var(--type-meta)",
            textAlign: "right",
          }}
        >
          {exp.label}
        </span>
        <ChevronRight
          size={16}
          strokeWidth={1.5}
          color="var(--fg-3)"
          aria-hidden
        />
      </Link>
    </li>
  );
}

function expiryStatus(expires: string | null): { label: string; color: string } {
  if (!expires) return { label: "—", color: "var(--fg-4)" };
  const d = new Date(expires + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return { label: `expired ${-days}d ago`, color: "var(--brick)" };
  if (days === 0) return { label: "today", color: "var(--brick)" };
  if (days <= 3) return { label: `${days}d`, color: "var(--brick)" };
  if (days <= 7) return { label: `${days}d`, color: "var(--amber)" };
  return { label: expires, color: "var(--fg-3)" };
}
